// ServiceProductsManager, Phase 1 retail upsell, provider side.
//
// Card that sits under the existing Services tab (alongside add-ons
// and consultation forms managers). For each service, the provider
// sees a small chip row of currently-recommended products and a
// dropdown to attach more from their own catalogue. Detach removes
// the chip.
//
// Backend endpoints used:
//   GET    /api/products                            (provider's catalogue)
//   GET    /api/services/{service_id}/products     (current attachments)
//   POST   /api/services/{service_id}/products     (attach)
//   DELETE /api/services/{service_id}/products/{product_id}  (detach)
import React, { useCallback, useEffect, useState } from 'react';
import { Package, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';

export function ServiceProductsManager({ services }) {
  const [catalogue, setCatalogue] = useState([]);
  // attachmentsByService is a map of service_id -> array of product objects
  const [attachmentsByService, setAttachmentsByService] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingPick, setPendingPick] = useState({}); // service_id -> selected product_id in the dropdown

  const fetchAll = useCallback(async () => {
    if (!services || services.length === 0) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [productsRes, ...attachRes] = await Promise.all([
        api.get('/products'),
        ...services.map((s) => api.get(`/services/${s.service_id}/products`).catch(() => ({ data: [] }))),
      ]);
      setCatalogue(Array.isArray(productsRes.data) ? productsRes.data : []);
      const next = {};
      services.forEach((s, i) => {
        next[s.service_id] = Array.isArray(attachRes[i].data) ? attachRes[i].data : [];
      });
      setAttachmentsByService(next);
    } catch (err) {
      console.error('ServiceProductsManager fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAttach = async (serviceId) => {
    const productId = pendingPick[serviceId];
    if (!productId) return;
    try {
      await api.post(`/services/${serviceId}/products`, { product_id: productId });
      toast.success('Product recommended');
      setPendingPick((s) => ({ ...s, [serviceId]: '' }));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not attach product');
    }
  };

  const handleDetach = async (serviceId, productId) => {
    try {
      await api.delete(`/services/${serviceId}/products/${productId}`);
      toast.success('Recommendation removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove recommendation');
    }
  };

  if (!services || services.length === 0) {
    return (
      <div className="text-center py-8 text-stone-500" data-testid="upsell-empty-services">
        <Package className="h-10 w-10 mx-auto mb-3 text-stone-300" />
        <p>Add a service first, then attach product recommendations to it.</p>
      </div>
    );
  }

  if (loading) {
    return <p className="text-sm text-stone-500" data-testid="upsell-loading">Loading recommendations...</p>;
  }

  if (catalogue.length === 0) {
    return (
      <div className="text-center py-8 text-stone-500" data-testid="upsell-empty-catalogue">
        <Package className="h-10 w-10 mx-auto mb-3 text-stone-300" />
        <p className="mb-3">Add a product to your catalogue first, then come back here to recommend it on a service.</p>
        <p className="text-xs text-stone-400">(Provider Dashboard &raquo; Products tab)</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="service-products-manager">
      <div>
        <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">Retail recommendations</h3>
        <p className="text-sm text-stone-500 mt-1">
          Recommend products to clients booking a specific service. They will see them on the booking summary and can request the ones they want, payable in person at the appointment.
        </p>
      </div>

      <div className="space-y-4">
        {services.map((service) => {
          const attached = attachmentsByService[service.service_id] || [];
          const attachedIds = new Set(attached.map((p) => p.product_id));
          const available = catalogue.filter((p) => !attachedIds.has(p.product_id));
          return (
            <div
              key={service.service_id}
              className="border border-[#E6E4DD] rounded-xl p-4"
              data-testid={`service-products-row-${service.service_id}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <p className="font-medium text-[#1A1A1A] truncate">{service.name}</p>
                <span className="text-xs text-stone-400 shrink-0">
                  {attached.length} recommended
                </span>
              </div>

              {/* Attached chips */}
              {attached.length === 0 ? (
                <p className="text-xs text-stone-400 mb-3">No recommendations yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                  {attached.map((p) => (
                    <span
                      key={p.product_id}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-[#F9F6F0] border border-[#D4AF37]/40 rounded-full text-sm"
                      data-testid={`upsell-chip-${service.service_id}-${p.product_id}`}
                    >
                      <span className="text-[#1A1A1A] truncate max-w-[16ch]">{p.name}</span>
                      <span className="text-[#D4AF37] font-semibold">£{Number(p.price).toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={() => handleDetach(service.service_id, p.product_id)}
                        className="text-stone-400 hover:text-red-600"
                        aria-label={`Remove ${p.name}`}
                        data-testid={`upsell-chip-remove-${service.service_id}-${p.product_id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Picker + attach */}
              {available.length > 0 ? (
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={pendingPick[service.service_id] || ''}
                    onChange={(e) => setPendingPick((s) => ({ ...s, [service.service_id]: e.target.value }))}
                    className="text-sm border border-[#E6E4DD] rounded-lg px-3 py-1.5 bg-white text-[#1A1A1A] flex-1 min-w-[180px]"
                    data-testid={`upsell-picker-${service.service_id}`}
                  >
                    <option value="">Pick a product...</option>
                    {available.map((p) => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name} - £{Number(p.price).toFixed(2)}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => handleAttach(service.service_id)}
                    disabled={!pendingPick[service.service_id]}
                    className="btn-primary"
                    data-testid={`upsell-attach-${service.service_id}`}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Recommend
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-stone-400">All catalogue products already recommended on this service.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
